<form
  id="add-airport-charge-form"
  class="office-settings-form"
  [formGroup]="addAirportChargeForm"
>
  <h2 i18n="@@office-charges.add-new-airport">Add New Airport</h2>
  <section>
    <h3>1. Select Airport & Currency</h3>
    <div class="d-flex" style="gap: 100px">
      <div>
        <p i18n="@@office-charges.add-charge-select-airport">
          Start adding service charges by selecting or searching an airport
        </p>
        <cai-autocomplete
          [width]="410"
          [hideLabel]="true"
          [options]="airportSelectorOptions"
          (onSelectItem)="onSelectAirport($event)"
          class="office-airport-charge-selector"
          placeholder="Select Airport"
          dropdownMenuClass="office-settings-autocomplete-menu"
          (focusout)="airportControl.markAsTouched()"
        >
        </cai-autocomplete>
        @if (airportControl?.hasError("required") && airportControl?.touched) {
          <mat-error> Please select an airport. </mat-error>
        }
      </div>
      <div>
        <p i18n="@@office-charges.add-charge-select-currency">
          Select the currency to apply on all service charges
        </p>
        <mat-select
          class="office-settings-form-field airport-currency-selector"
          panelClass="office-settings-panel office-service-panel"
          formControlName="currencyCode"
        >
          @for (currency of allCurrencies; track currency) {
            <mat-option
              [value]="currency.currencyCode"
              class="office-settings-select-option"
            >
              {{ currency.currencyCode }}
            </mat-option>
          }
        </mat-select>
      </div>
    </div>
  </section>
  <section>
    <h3>2. Add Services</h3>
    <p>Add at least one service charge for the new airport</p>
    <div style="overflow-x: auto; overflow-y: clip">
      <table
        id="office-services-table"
        class="table table-hover table-checkable sortable table-responsive office-settings-table"
      >
        <thead>
          <tr>
            <th id="col-services" scope="col">
              <span i18n="@@office-charges.services">Services</span>
              <span style="color: red">*</span>
            </th>
            <th id="col-buying-rate" scope="col">
              <span i18n="@office-charges.buying-rate">Buying Rate</span>
            </th>
            <th id="col-flat-rate" scope="col">
              <span i18n="@office-charges.markup">Markup</span>
            </th>
            <th id="col-selling-rate" scope="col">
              <span i18n="@office-charges.selling-rate">Selling Rate</span>
              <span style="color: red">*</span>
            </th>
            <th id="col-min" scope="col" i18n="@@office-charges.min">Min</th>
            <th id="col-max" scope="col" i18n="@@office-charges.max">Max</th>
            <th id="col-uom" scope="col">
              <span i18n="@@office-charges.uom">UOM</span>
              <span style="color: red">*</span>
            </th>
            <th id="col-type" scope="col">
              <span i18n="@@office-charges.type">Type</span>
              <span style="color: red">*</span>
            </th>
            <th id="col-commodity" scope="col" i18n="@@office-charges.default">
              Default
            </th>
            <th id="col-fill"></th>
          </tr>
        </thead>
        <tbody>
          <tr formGroupName="firstCharge">
            <td headers="col-services">
              <input
                formControlName="name"
                (mouseover)="onHoverChargesField('name')"
                [matTooltip]="officeServiceTooltipError"
                [matTooltipDisabled]="officeServiceTooltipError.length === 0"
              />
            </td>
            <td headers="col-buying-rate">
              <input
                formControlName="buyingRate"
                (mouseover)="onHoverChargesField('buyingRate')"
                [matTooltip]="officeServiceTooltipError"
                [matTooltipDisabled]="officeServiceTooltipError.length === 0"
              />
            </td>
            <td headers="col-flat-rate">
              <div class="markup-control">
                <input
                  formControlName="markup"
                  (mouseover)="onHoverChargesField('markup')"
                  [matTooltip]="officeServiceTooltipError"
                  [matTooltipDisabled]="officeServiceTooltipError.length === 0"
                />
    
                <div class="markup-flat-radio-button">
                  <label for="isMarkupFlat">$</label>
                  <input
                    type="radio"
                    formControlName="isMarkupFlat"
                    id="isMarkupFlat"
                    [value]="true"
                  />
                </div>
                <div class="markup-flat-radio-button">
                  <label for="isMarkupPercent">%</label>
                  <input
                    type="radio"
                    formControlName="isMarkupFlat"
                    id="isMarkupPercent"
                    [value]="false"
                  />
                </div>
              </div>
            </td>
            <td headers="col-selling-rate">
              <input
                formControlName="sellingRate"
                (mouseover)="onHoverChargesField('sellingRate')"
                [matTooltip]="officeServiceTooltipError"
                [matTooltipDisabled]="officeServiceTooltipError.length === 0"
              />
            </td>
            <td headers="col-min">
              <input
                formControlName="markupMinFlat"
                (mouseover)="onHoverChargesField('markupMinFlat')"
                [matTooltip]="officeServiceTooltipError"
                [matTooltipDisabled]="officeServiceTooltipError.length === 0"
              />
            </td>
            <td headers="col-max">
              <input
                formControlName="markupMaxFlat"
                (mouseover)="onHoverChargesField('markupMaxFlat')"
                [matTooltip]="officeServiceTooltipError"
                [matTooltipDisabled]="officeServiceTooltipError.length === 0"
              />
            </td>
            <td headers="col-uom">
              <mat-select
                formControlName="unitOfMeasure"
                (mouseover)="onHoverChargesField('unitOfMeasure')"
                [matTooltip]="officeServiceTooltipError"
                [matTooltipDisabled]="officeServiceTooltipError.length === 0"
                panelClass="office-settings-panel office-service-panel"
              >
                @for (enum of officeChargeUnitOfMeasure | keyvalue; track enum) {
                  <mat-option
                    [value]="enum.key"
                    class="office-settings-select-option"
                  >
                    {{ enum.value }}
                  </mat-option>
                }
              </mat-select>
            </td>

            <td headers="col-type">
              <mat-select
                formControlName="chargeCategory"
                (mouseover)="onHoverChargesField('chargeCategory')"
                placeholder="Type"
                [matTooltip]="officeServiceTooltipError"
                [matTooltipDisabled]="officeServiceTooltipError.length === 0"
                panelClass="office-settings-panel office-service-panel"
              >
                @for (enum of officeChargeCategory | keyvalue; track enum) {
                  <mat-option
                    [value]="enum.key"
                    class="office-settings-select-option"
                  >
                    {{ enum.value }}
                  </mat-option>
                }
              </mat-select>
            </td>
            <td headers="col-commodity">
              <mat-checkbox
                formControlName="isDefault"
                (mouseover)="onHoverChargesField('isDefault')"
                [matTooltip]="officeServiceTooltipError"
                [matTooltipDisabled]="officeServiceTooltipError.length === 0"
              >
              </mat-checkbox>
              @if (addAirportChargeForm?.get("firstCharge.isDefault")?.value) {
                <mat-select
                  style="width: 164px"
                  placeholder="Goods Type"
                  formControlName="defaultCommodity"
                  (mouseover)="onHoverChargesField('defaultCommodity')"
                  [matTooltip]="officeServiceTooltipError"
                  [matTooltipDisabled]="officeServiceTooltipError.length === 0"
                  panelClass="office-settings-panel office-service-panel"
                  class="show-errors-asap"
                >
                  @for (enum of commodityType | keyvalue; track enum) {
                    <mat-option
                      [value]="enum.key"
                      class="office-settings-select-option"
                    >
                      {{ enum.value }}
                    </mat-option>
                  }
                </mat-select>
              }
            </td>
            <td headers="col-fill"></td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
  <div class="d-flex" style="gap: 20px">
    <button
      class="btn btn-confirm"
      type="submit"
      i18n="@@global.confirm"
      [disabled]="addAirportChargeForm?.invalid"
      (click)="createOfficeAirportChargeFromForm()"
    >
      Confirm
    </button>
    <button
      class="btn btn-cancel"
      type="button"
      (click)="modalRef.dismiss()"
      i18n="@@global.cancel"
    >
      Cancel
    </button>
  </div>
</form>
